import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

const SearchableSelect = ({ value, onChange, options, placeholder = 'Select...', displayValue, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (e.key === 'ArrowDown' && filteredOptions.length > 0 && !isOpen) {
      setIsOpen(true);
    }
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          position: 'relative',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className="form-input"
          value={isOpen ? searchTerm : (displayValue || selectedOption?.label || '')}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => !disabled && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            paddingRight: 'calc(var(--space-lg) + var(--space-md))',
            opacity: disabled ? 0.5 : 1,
          }}
          autoComplete="off"
        />
        <div style={{
          position: 'absolute',
          right: 'var(--space-sm)',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-xs)',
          pointerEvents: 'none',
        }}>
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                pointerEvents: 'auto',
                background: 'none',
                border: 'none',
                padding: 'var(--space-xs)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--color-text-tertiary)',
              }}
              title="Clear selection"
            >
              <XMarkIcon style={{ width: 14, height: 14 }} />
            </button>
          )}
          <ChevronDownIcon
            style={{
              width: 16,
              height: 16,
              color: 'var(--color-text-tertiary)',
              transition: 'transform var(--transition-fast)',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </div>
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--space-xs))',
            left: 0,
            right: 0,
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: 'var(--radius-md)',
            maxHeight: '250px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => handleSelect(option.value)}
                style={{
                  padding: 'var(--space-sm) var(--space-md)',
                  cursor: 'pointer',
                  background: option.value === value ? 'var(--color-bg-tertiary)' : 'transparent',
                  transition: 'background var(--transition-fast)',
                  fontSize: '0.875rem',
                }}
                onMouseEnter={(e) => {
                  if (option.value !== value) {
                    e.currentTarget.style.background = 'var(--color-bg-tertiary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (option.value !== value) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div
              style={{
                padding: 'var(--space-md)',
                textAlign: 'center',
                color: 'var(--color-text-tertiary)',
                fontSize: '0.875rem',
              }}
            >
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
